import { useCallback, useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { OnlineStatus } from '@/modules/car/model';
import { IDriver } from '@/modules/driver/model/driver';
import { getCityTimezone } from '@/modules/partner/store/selectors';
import { EntityPage } from '@/shared/models/entity-page';
import { AccessStatus } from '@/shared/view/AccessStatus';
import { CarCell } from '@/shared/view/CarCell';
import { IFilterItem } from '@/shared/view/ListLayout/Filters';
import { IColumnConfig } from '@/shared/view/ListLayout/List';
import { ListLayoutView } from '@/shared/view/ListLayout/ListLayout';
import { MobileListItem } from '@/shared/view/ListLayout/MobileListItem';
import { StatusTag } from '@/shared/view/StatusTag';
import { TransferStatus } from '@/shared/view/TransferStatus';
import { Icon } from '@src-common/view/components/Icon';

import { Avatar } from './components/Avatar';
import { LastActiveCell } from './components/LastActiveCell';
import { Phone } from './components/Phone';
import s from './styles.module.css';

type Props = {
  drivers: EntityPage<IDriver>;
  fetchDrivers: (_: any, pagination: { offset: number; limit: number }) => void;
  onClickNew: () => void;
  isLoading?: boolean;
};

type Filters = 'search';

const GROUP_ORDER: OnlineStatus[] = ['online', 'busy', 'offline'];

const filterConfig: IFilterItem<Filters>[] = [
  {
    title: <FormattedMessage defaultMessage="Search" id="Fk2UcG" description="Filter title" />,
    name: 'search',
    type: 'text',
    initialValue: '',
  },
];

const getFullName = (driver: IDriver) => [driver.firstName, driver.lastName].join(' ');

const View = ({ drivers, fetchDrivers, onClickNew, isLoading = false }: Props) => {
  const history = useHistory();
  const timezone = useSelector(getCityTimezone);

  const columns: IColumnConfig<IDriver>[] = useMemo(
    () => [
      {
        renderHeader: () => <></>,
        renderCell: driver => <Avatar photoURL={driver.photoURL} />,
        style: { width: '64px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Name"
            id="SfHYNA"
            description="Drivers: column header"
          />
        ),
        renderCell: getFullName,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Rating"
            id="6jQUMP"
            description="Drivers: column header"
          />
        ),
        renderCell: driver => (
          <FormattedNumber
            value={driver.rating}
            maximumFractionDigits={2}
            minimumFractionDigits={2}
          />
        ),
        style: { width: '84px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Vehicle"
            id="ENwC6W"
            description="Drivers: column header"
          />
        ),
        renderCell: driver => <CarCell carId={driver.carId} />,
        style: { width: '258px' },
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Last activity"
            id="ICsLCD"
            description="Drivers: column header"
          />
        ),
        renderCell: driver => <LastActiveCell lastActivity={driver.lastActivity} />,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Phone number"
            id="9o7Uk1"
            description="Drivers: column header"
          />
        ),
        renderCell: driver => <Phone phone={driver.phone} />,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="Status"
            id="ixac+i"
            description="Drivers: column header"
          />
        ),
        renderCell: driver => (
          <div className={s.status}>
            <StatusTag status={driver.onlineStatus} />
            <AccessStatus status={driver.accessStatus} />
            {driver.transferRequest && (
              <TransferStatus date={driver.transferRequest.leavingAtDate} timezone={timezone} />
            )}
          </div>
        ),
      },
    ],
    [timezone],
  );

  const onClickRow = useCallback(
    (driver: IDriver | undefined) => driver && history.push(`/chauffeurs/${driver.id}`),
    [history],
  );

  return (
    <ListLayoutView<IDriver, Filters>
      wrapperClassName={s.wrapper}
      title={<FormattedMessage defaultMessage="Chauffeurs" id="PdVFfx" description="Page title" />}
      data={drivers.items}
      total={drivers.total}
      limit={drivers.limit}
      columns={columns}
      onFetchData={fetchDrivers}
      getEntityId={driver => String(driver.id)}
      shouldBeEmptyWithoutFilters={false}
      groupOrder={GROUP_ORDER}
      getEntityGroup={driver => driver.onlineStatus}
      filterConfig={filterConfig}
      renderItem={driver => (
        <Link to={`/chauffeurs/${driver.id}`}>
          <MobileListItem
            title={
              <>
                {getFullName(driver)} <AccessStatus status={driver.accessStatus} />
                {driver.transferRequest && (
                  <TransferStatus date={driver.transferRequest.leavingAtDate} timezone={timezone} />
                )}
              </>
            }
            note={
              <FormattedNumber
                value={driver.rating}
                maximumFractionDigits={2}
                minimumFractionDigits={2}
              />
            }
            right={<Icon icon={'chevron-to-end'} />}
          />
        </Link>
      )}
      onClickRow={onClickRow}
      action={useMemo(
        () => ({
          onClick: onClickNew,
          title: (
            <FormattedMessage
              defaultMessage="Add new"
              id="IoXTmo"
              description="Create driver button"
            />
          ),
          className: s.action,
        }),
        [onClickNew],
      )}
      isLoading={isLoading}
    />
  );
};

export { View };
